import { faCheck, faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';

import Image from '../Image';
import Link from '../Link';

interface Props {
  imageSrc?: string;
  imageAlt?: string;
  title: string;
  optionId: string;
  onClick: (id: string) => void;
  tooltip?: boolean;
  active?: boolean;
  className?: string;
  onToolTipClick?: () => void;
  hideImage?: boolean;
  titleAs?: keyof JSX.IntrinsicElements;
}

const LinkStyles = cva('shadow-l overflow-hidden relative rounded-brand flex h-full flex-col rounded-bl-none', {
  variants: {
    active: { true: 'border-optionCard-active-border border-[3px]' },
  },
});

const Title = ({ title, titleAs = 'h4' }: { title: string; titleAs?: keyof JSX.IntrinsicElements }) => {
  const Heading: 'h4' | 'h6' = titleAs as 'h4' | 'h6';

  return (
    <div className="rounded-br-brand inline-flex min-h-20 items-center bg-white py-4 pl-6 pr-8">
      <Heading>{title}</Heading>
    </div>
  );
};

const OptionCard = ({
  onToolTipClick,
  imageSrc,
  imageAlt,
  title,
  optionId,
  onClick,
  tooltip = false,
  active = false,
  className,
  hideImage,
  titleAs,
}: Props) => {
  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onToolTipClick?.();
  };

  const renderImage = () => {
    if (hideImage) {
      return null;
    }

    return (
      <div className="h-75 w-full bg-red-900">
        {imageSrc && imageAlt && (
          <Image alt={imageAlt} className="rounded-brand h-full w-full rounded-b-none object-cover" src={imageSrc} />
        )}
      </div>
    );
  };

  const renderTooltipButton = () => {
    if (!tooltip) {
      return null;
    }

    return (
      <button
        aria-label="tooltip-button"
        className="absolute bottom-12 right-4 z-10 inline-flex"
        onClick={handleClick}
        type="button"
      >
        <FontAwesomeIcon className="text-optionCard-info-icon-text text-base" icon={faCircleInfo} />
      </button>
    );
  };

  const renderActiveIndicator = () => {
    if (!active) {
      return null;
    }

    return (
      <span className="bg-optionCard-selected-badge-bg absolute bottom-5 right-5 ml-7 inline-flex h-4 min-h-4 w-4 min-w-4 items-center justify-center rounded-full">
        <FontAwesomeIcon className="h-2 w-2 text-white" icon={faCheck} />
      </span>
    );
  };

  return (
    <div className={twMerge(LinkStyles({ active }), className)} data-card data-testid="paintguide-answer">
      {renderTooltipButton()}
      <Link onClick={() => onClick(optionId)}>
        {renderImage()}
        <div className="relative">
          <Title title={title} titleAs={titleAs} />
        </div>
        {renderActiveIndicator()}
      </Link>
    </div>
  );
};

export default OptionCard;
